import React, { useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { Send, Mail, MapPin, Phone, Terminal, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', msg: '' });

    try {
      await axiosInstance.post('/contact', formData);
      setStatus({ type: 'success', msg: 'Transmission Successful. We will respond shortly.' });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', msg: 'Transmission Failed. Check connection protocols.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 relative font-sans overflow-hidden py-16 px-4">
      
      {/* Background Decor */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(34,211,238,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-0"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        
        {/* Header */}
        <div className="text-center mb-16">
           <div className="inline-flex items-center gap-2 text-[#22d3ee] font-mono text-xs mb-4 border border-[#22d3ee]/30 px-3 py-1 rounded-full bg-[#22d3ee]/5">
              <Terminal size={12} /> INITIALIZE_COMMS
           </div>
           <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
             Get In <span className="text-[#22d3ee]">Touch</span>
           </h1>
           <p className="text-slate-400 max-w-lg mx-auto">
             Have a project in mind or need technical assistance? Establish a connection with our team below.
           </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* --- LEFT: INFO PANEL --- */}
          <div className="space-y-8">
             <div className="bg-[#0f172a] border border-slate-800 p-8 rounded-lg relative overflow-hidden group hover:border-[#22d3ee]/50 transition-colors">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                   <Mail size={80} className="text-[#22d3ee]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                   <span className="w-1 h-6 bg-[#22d3ee]"></span> Contact Nodes
                </h3>
                
                <div className="space-y-6">
                   <InfoItem icon={<Mail/>} label="Email Protocol" value="mecode786@gmail.com" />
                   {/* <InfoItem icon={<Phone/>} label="Voice Line" value="+1 (555) 000-0000" /> */}
                   <InfoItem icon={<MapPin/>} label="Base Coordinates" value="Kolkata, India, WB" />
                </div>
             </div>

             {/* Decorative Map/Visual */}
             <div className="h-64 bg-[#0f172a] border border-slate-800 rounded-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#22d3ee] via-transparent to-transparent"></div>
                <p className="text-[#22d3ee] font-mono text-xs tracking-widest animate-pulse">
                   [ TEXT_US_TO_CONNECT... ]
                </p>
             </div>
          </div>

          {/* --- RIGHT: FORM --- */}
          <div className="bg-[#0f172a]/80 backdrop-blur-md border border-slate-800 p-8 rounded-lg shadow-2xl">
             <form onSubmit={handleSubmit} className="space-y-6">
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <InputGroup label="User ID (Name)" name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange} />
                   <InputGroup label="Comms Link (Email)" name="email" type="email" placeholder="john@example.com" value={formData.email} onChange={handleChange} />
                </div>
                
                <InputGroup label="Subject Header" name="subject" type="text" placeholder="Project Inquiry / Support" value={formData.subject} onChange={handleChange} />
                
                <div>
                   <label className="block text-[#22d3ee] text-xs font-mono mb-2 uppercase tracking-widest">Message Data</label>
                   <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      placeholder="Input message content..."
                      className="w-full bg-[#020617] border border-slate-700 text-slate-300 p-3 rounded-sm focus:border-[#22d3ee] focus:ring-1 focus:ring-[#22d3ee] focus:outline-none transition-all font-mono text-sm"
                      required
                   ></textarea>
                </div>

                <button 
                   type="submit" 
                   disabled={isSubmitting}
                   className="w-full bg-[#22d3ee] hover:bg-[#06b6d4] text-[#020617] font-bold py-4 px-6 rounded-sm uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                   {isSubmitting ? 'Transmitting...' : <><Send size={16} /> Send Transmission</>}
                </button>

                {/* Status Message */}
                {status.msg && (
                   <div className={`mt-4 p-4 border-l-4 flex items-center gap-3 ${status.type === 'success' ? 'bg-green-900/20 border-green-500 text-green-400' : 'bg-red-900/20 border-red-500 text-red-400'}`}>
                      {status.type === 'success' ? <CheckCircle size={20}/> : <AlertCircle size={20}/>}
                      <span className="text-sm font-mono">{status.msg}</span>
                   </div>
                )}

             </form>
          </div>

        </div>
      </div>
    </div>
  );
};

// Helper Component for inputs
const InputGroup = ({ label, name, type, placeholder, value, onChange }) => (
   <div>
      <label className="block text-[#22d3ee] text-xs font-mono mb-2 uppercase tracking-widest">{label}</label>
      <input 
         type={type} 
         name={name}
         value={value}
         onChange={onChange}
         placeholder={placeholder} 
         className="w-full bg-[#020617] border border-slate-700 text-slate-300 p-3 rounded-sm focus:border-[#22d3ee] focus:ring-1 focus:ring-[#22d3ee] focus:outline-none transition-all font-mono text-sm"
         required
      />
   </div>
);

const InfoItem = ({ icon, label, value }) => (
   <div className="flex items-start gap-4">
      <div className="p-3 bg-[#020617] border border-slate-700 text-slate-400 rounded-lg">
         {React.cloneElement(icon, { size: 20 })}
      </div>
      <div>
         <p className="text-xs text-[#22d3ee] font-mono uppercase tracking-wide mb-1">{label}</p>
         <p className="text-white font-medium">{value}</p>
      </div>
   </div>
);

export default Contact;